/**
 * The trainer step (name and level), and when it is asked for.
 *
 * NOW (owner direction, 2026-10-02)
 *   The step is two typed fields: the trainer's Pokémon GO name and level. No
 *   screenshot (lib/trainerSetup). It is asked for at sign-up: AuthProvider
 *   requests it the moment an account with no trainer name signs up or in.
 *   "Not now" still closes it, and Live Scan asks again, as below. The rest of
 *   this note is how the request store came to exist, and it still describes
 *   how the store behaves.
 *
 * HISTORY
 *   Until 2026-08-17 the profile screenshot was a hard wall straight after
 *   sign-up: leave the app, open Pokémon GO, screenshot your profile, come
 *   back, before seeing anything the app does. "Look around first" then made
 *   it skippable, but the wall still came first.
 *
 * WHY IT MOVED (owner direction, 2026-10-01)
 *   Production said the wall was still where people were lost: about 15% of
 *   sign-ups never uploaded it, and of ~111 such accounts exactly ONE ever
 *   came back. 51 accounts accepted the terms and never viewed a single page.
 *   So sign-up no longer asks at all. The trainer goes straight into the app
 *   (and the tour), and the profile is asked for at the one place that
 *   genuinely needs it: Live Scan, because a scan writes to a dex and the
 *   profile is what a dex belongs to.
 *
 * HOW
 *   ProfileGate renders nothing unless the step has been REQUESTED on this
 *   device. Sign-up requests it, and so does Live Scan's "Add my trainer name";
 *   "Not now" on the step dismisses the request, and finishing it makes the
 *   gate moot. Keyed by account id, so on a shared device one
 *   trainer's request never opens the sheet for another.
 *
 *   localStorage rather than the server because it is UI state, not a property
 *   of the account. Changes are broadcast so the gate re-renders the moment
 *   the request is made, wherever the request came from.
 */

export const PROFILE_REQUEST_KEY = "nexus.profileSetupRequested.v1";
const CHANGE_EVENT = "nexus:profile-setup-request";

function read(): number[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(PROFILE_REQUEST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === "number") : [];
  } catch {
    return [];
  }
}

function write(ids: number[]): void {
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(PROFILE_REQUEST_KEY, JSON.stringify(ids));
    } catch {
      /* quota: worst case the sheet does not open and Live Scan asks again */
    }
  }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Has the trainer step been asked for on this device (and not yet put off with "Not now")? */
export function isProfileSetupRequested(accountId: number | null | undefined): boolean {
  if (accountId == null) return false;
  return read().includes(accountId);
}

/** Open the step: the account just signed up, or asked for something that needs a trainer name. */
export function requestProfileSetup(accountId: number | null | undefined): void {
  if (accountId == null) return;
  const ids = read();
  if (ids.includes(accountId)) {
    write(ids); // still broadcast, so a gate that missed it re-checks
    return;
  }
  write([...ids, accountId]);
}

/** "Not now", or setup finished: close it until the next ask. */
export function dismissProfileSetupRequest(accountId: number | null | undefined): void {
  if (accountId == null) return;
  const ids = read();
  if (!ids.includes(accountId)) return;
  write(ids.filter((id) => id !== accountId));
}

// ── "Not now", for an account that has never typed its name and level ───────
//
// Since 2026-10-02 every account that has not typed its trainer name and level
// is asked when the app opens (lib/trainerSetup.trainerStepKind). "Not now"
// puts that off until the app is next opened: held in memory, on purpose, so
// it does not outlive the launch. Persisting it would turn one tap into
// "never ask", and the name is what friends find a trainer by.
const deferredThisLaunch = new Set<number>();

/** Has this account put the name-and-level question off since the app was opened? */
export function isTrainerConfirmDeferred(accountId: number | null | undefined): boolean {
  return accountId != null && deferredThisLaunch.has(accountId);
}

/** "Not now" on the step: stay shut until the next launch. */
export function deferTrainerConfirm(accountId: number | null | undefined): void {
  if (accountId == null) return;
  deferredThisLaunch.add(accountId);
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** For useSyncExternalStore: re-render when a request is made or dismissed. */
export function subscribeProfileSetupRequest(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === PROFILE_REQUEST_KEY) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
  };
}

/** Test seam. */
export function _resetProfileSetupRequestsForTests(): void {
  deferredThisLaunch.clear();
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(PROFILE_REQUEST_KEY);
  } catch {
    /* ignore */
  }
}
